import "./workView.scss";

import { isEqual } from "lodash";
import memoizeOne from "memoize-one";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import { connect } from "react-redux";

import { ContainerContext, withContainerContext } from "../../components/container/ContainerContext";
import LongPressCapture from "../../components/longPressCapture";
import { withScreenContext } from "../../components/screen/ScreenContext";
import Dimensions, { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import { preferencesSelectors } from "../../modules/preferences";
import { PropTypeShapes } from "../../types";
import { chooseValueForIos, mergeDispatchToProps } from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";
import SmartErrorBoundary from "../smartErrorBoundary";
import WorkViewHeader from "./workViewHeader";

const wrapHandlerToCatchFallthrough = func => {
  return func
    ? e => {
      e.stopPropagation();
      func(e.altKey);
    }
    : null;
};

const isMobile = preprocessorQuery.isMobile();

const getContainerRectStates = (containerRect, globalFontSize) => {
  if (!containerRect) {
    return {};
  }
  const SCALED_WORKVIEW_HEADER_HEIGHT = scalePixelsToFontSize(Dimensions.WORKVIEW_HEADER_HEIGHT, globalFontSize);

  const headerContainerRect = containerRect.withMutations(r => {
    const t = r.top;
    const h = r.height - SCALED_WORKVIEW_HEADER_HEIGHT;
    r.set("top", t);
    r.set("height", h);
    r.set("bottom", t + h);
  });

  const workspaceContainerRect = isMobile
    ? containerRect
    : containerRect.withMutations(r => {
      const t = r.top + SCALED_WORKVIEW_HEADER_HEIGHT;
      const h = r.height - SCALED_WORKVIEW_HEADER_HEIGHT;
      r.set("top", t);
      r.set("height", h);
      r.set("bottom", t + h);
    });

  return {
    headerContainerRect,
    workspaceContainerRect
  };
};

const getContainerContextValue = memoizeOne((stateContainerRect, paddingLeft, paddingRight) => {
  return { containerRect: stateContainerRect, paddingLeft, paddingRight };
});

class WorkView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = Object.assign(getContainerRectStates(props.containerRect, props.globalFontSize, props.screenContext), {
      popoverOpen: false
    });

    this.toggleSettings = this.toggleSettings.bind(this);

    //#!if iOS === true
// this.onLongPress = this.onLongPress.bind(this);
    //#!endif
  }

  componentDidMount() {
    if (
      this.props.onResize &&
      (this.props.containerRect.width !== this.props.storedSize.width ||
        this.props.containerRect.height !== this.props.storedSize.height)
    ) {
      this.props.onResize(this.props.containerRect);
    }
  }

  componentWillReceiveProps(nextProps) {
    let s = getContainerRectStates(nextProps.containerRect, nextProps.globalFontSize, nextProps.screenContext);
    if (isEqual(s.headerContainerRect, this.state.headerContainerRect)) {
      delete s.headerContainerRect;
    }
    if (isEqual(s.workspaceContainerRect, this.state.workspaceContainerRect)) {
      delete s.workspaceContainerRect;
    }
    this.setState(s);
  }

  componentDidUpdate() {
    if (
      this.props.onResize &&
      (this.props.containerRect.width !== this.props.storedSize.width ||
        this.props.containerRect.height !== this.props.storedSize.height)
    ) {
      this.props.onResize(this.props.containerRect);
    }
  }

  //#!if iOS === true
// onLongPress() {
// if (this.props.screenContext.enterEditMode) {
// this.props.screenContext.enterEditMode();
// }
// }
  //#!endif

  toggleSettings() {
    this.setState({
      popoverOpen: !this.state.popoverOpen
    });
  }

  render() {
    const appStyle = {
      fontSize: `${this.props.fontSize}px`,
      lineHeight: `${Dimensions.BASE_LINE_HEIGHT}em`
    };

    let showHeader = true;
    //#!if mobile === true
// showHeader = false;
    //#!endif

    const containerContextValue = getContainerContextValue(
      this.state.workspaceContainerRect,
      this.props.paddingLeft,
      this.props.paddingRight
    );

    let result = (
      <div className={"workView" + (this.props.isActive ? " isActive" : "")} onClick={this.props.onClick}>
        {showHeader ? (
          <WorkViewHeader
            label={this.props.label}
            labelColor={this.props.labelColor}
            showClose={this.props.allowClose}
            onClose={wrapHandlerToCatchFallthrough(this.props.onClose)}
            showSplitHorizontal={this.props.allowSplitHorizontal}
            showSplitVertical={this.props.allowSplitVertical}
            showMinimize={this.props.allowMinimize}
            onMinimize={wrapHandlerToCatchFallthrough(this.props.onMinimize)}
            isMinimized={this.props.isMinimized}
            onSplitHorizontal={wrapHandlerToCatchFallthrough(this.props.onSplitHorizontal)}
            onSplitVertical={wrapHandlerToCatchFallthrough(this.props.onSplitVertical)}
            onPopout={wrapHandlerToCatchFallthrough(this.props.onPopout)}
            onFilter={this.props.onFilter}
            onEditTitle={this.props.onEditTitle}
            containerRect={this.state.headerContainerRect}
            subLabel={this.props.subLabel}
            subLabelIcon={this.props.subLabel ? "toolbarFilter" : null}
            onSubLabelClick={this.props.openLocalFilterPanel}
            isActive={this.props.isActive}
            globalFontSize={this.props.globalFontSize}
          />
        ) : null}
        <div className={"workspace" + (this.props.isActive ? " isActive" : "")} style={appStyle}>
          <ContainerContext.Provider value={containerContextValue}>
            <SmartErrorBoundary fixed={true}>{this.props.children}</SmartErrorBoundary>
          </ContainerContext.Provider>
        </div>
      </div>
    );

    result = chooseValueForIos(
      <LongPressCapture
        disabled={this.props.screenContext && this.props.screenContext.isEditMode}
        onLongPress={this.onLongPress}
      >
        {result}
      </LongPressCapture>,
      result
    );

    return result;
  }
}

WorkView.propTypes = {
  children: PropTypes.element,
  isActive: PropTypes.bool.isRequired,
  isMinimized: PropTypes.bool,
  viewId: PropTypes.string.isRequired,
  //#!if mobile === true
// label: PropTypes.string,
// labelColor: PropTypes.string,
// subLabel: PropTypes.string,
  //#!endif
  storedSize: PropTypeShapes.size.isRequired,
  allowSplitHorizontal: PropTypes.bool.isRequired,
  allowSplitVertical: PropTypes.bool.isRequired,
  allowMinimize: PropTypes.bool.isRequired,
  allowClose: PropTypes.bool.isRequired,
  onEditTitle: PropTypes.func,

  onClick: PropTypes.func,
  onClose: PropTypes.func,
  onMinimize: PropTypes.func,
  onPopout: PropTypes.func,
  onSplitHorizontal: PropTypes.func,
  onSplitVertical: PropTypes.func,
  onFilter: PropTypes.func,
  onResize: PropTypes.func,
  openLocalFilterPanel: PropTypes.func,

  fontSize: PropTypes.number.isRequired,
  globalFontSize: PropTypes.number.isRequired,

  // Comes from ContainerContext
  containerRect: PropTypeShapes.flatRect.isRequired,
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number,

  // From withScreenContext
  screenContext: PropTypes.object
};

const mapStateToProps = (state, props) => {
  return {
    //#!if mobile !== true
    label: appStateSelectors.getViewLabel(state.present, props.viewId),
    subLabel: appStateSelectors.getViewSubLabel(state.present, props.viewId),
    labelColor: appStateSelectors.getViewLabelColor(state.present, props.viewId),
    //#!endif
    fontSize: appStateSelectors.getFontSize(state.present),
    globalFontSize: preferencesSelectors.getGlobalFontSize(state.present)
  };
};

const readonlyActions = {
  openLocalFilterPanel: appStateOperations.openLocalFilterPanel
};

const editingActions = {};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

const regularWorkView = connect(mapStateToProps, mapDispatchToProps)(withContainerContext(WorkView));
const finalWorkView = withScreenContext(regularWorkView);

export default finalWorkView;
